import { useContext, Suspense } from 'react';
import SWSLogo from '@assets/images/logo/sws-logo-updated.svg';
import ChevronLeftSolid from '@simplywallst/ui-core/icons/ChevronLeftSolid';
import loadable from '@loadable/component';
import { cn, Link } from '@simplywallst/ui-core/next';
import useUser from '@/hooks/useUser';
import { useUserAccess } from '@/hooks/useUserAccess';
import LayoutContext from '@/components/Layout/context/LayoutContext/LayoutContext';
import { useTracking } from 'react-tracking';
import { useSelector } from 'react-redux';
import { useFlag } from '@/features/unleash';
import { getCanGoBack } from '@/components/Nav/MobileTopbar/redux/selectors';
import { useLocale } from '@/hooks/useLocale';
import { isPageTranslated } from '@/utils/locale';
import { useLocation } from '@/hooks/useLocation';
import { UpgradeButton } from '@/components/Nav/DesktopTopbar/components/UpgradeButton';
import { getPath } from '@/router/utils';
import {
  ROUTE_DASHBOARD,
  ROUTE_WELCOME,
  URL_SALES_SITE,
} from '@/constants/routes';

const Nav = loadable(() => import('../Nav'), {
  resolveComponent: (component) => component.Nav,
});

const BasicSearch = loadable(
  () => import('@/components/Nav/DeprecatedSearch/components/BasicSearch'),
  { ssr: false }
);

const DefaultState = loadable(
  () => import('@/components/Nav/DeprecatedSearch/components/DefaultState')
);

const DesktopTopbarAccountAccess = loadable(
  () =>
    import(
      '@/components/Nav/DesktopTopbar/components/DesktopTopbarAccountAccess'
    )
);

const DesktopTopbarLoggedOut = loadable(
  () =>
    import('@/components/Nav/DesktopTopbar/components/DesktopTopbarLoggedOut'),
  { resolveComponent: (c) => c.DesktopTopbarLoggedOut }
);

const LanguageButton = loadable(
  () => import('@/components/Nav/DesktopTopbar/components/LanguageButton')
);

export const TopBar = () => {
  const languageDropdownFlag = useFlag({ name: 'language-dropdown' });
  const locationState = useLocation();
  const showLanguageDropdown =
    languageDropdownFlag.enabled && isPageTranslated(locationState.type);
  const { trackEvent } = useTracking();
  const layout = useContext(LayoutContext);
  const user = useUser();
  const access = useUserAccess();

  const canGoBack = useSelector(getCanGoBack);
  const location = locationState?.type ?? '';

  const locale = useLocale();

  return (
    <header
      className="flex h-x8 min-h-x8 flex-wrap justify-center border-0 border-b border-solid border-legacy-white/10 bg-legacy-charcoal text-sm text-legacy-white data-[theme]:border-b-[var(--s-nav-border-color)] data-[theme]:bg-[var(--s-nav-bg-color)] data-[theme]:text-[var(---s-nav-text-color)]"
      id="desktop-topbar-wrapper"
      data-cy-id="main-navigation-wrapper"
    >
      <div
        className={cn(
          'relative w-full transition-all duration-[.6s] ease-[cubic-bezier(0.83,0,0.17,1)]',
          layout.wider
            ? 'max-[1439px]:max-w-screen-xl min-[1440px]:max-[1582px]:max-w-[1375px] min-[1582px]:max-w-[1582px]'
            : 'max-w-screen-xl'
        )}
      >
        {canGoBack && (
          <div className="absolute left-0 top-x1_5 border-r border-r-legacy-darkergray">
            <button
              className="relative size-x5 px-x1_5"
              onClick={() => {
                window.history.back();
              }}
            >
              <ChevronLeftSolid className="absolute left-x1 top-x1 size-x3 fill-legacy-white" />
            </button>
          </div>
        )}
        <div
          className={cn(
            'absolute top-[9px] overflow-x-hidden pt-px max-xl:w-[30px]',
            canGoBack ? 'left-x7' : 'left-x3'
          )}
        >
          <Link
            href={
              RUNTIME_ENV === 'native'
                ? getPath({
                    type: user.data.loggedIn ? ROUTE_DASHBOARD : ROUTE_WELCOME,
                    payload: {},
                  })
                : URL_SALES_SITE
            }
            className="block"
            onClick={() => trackEvent({ action: 'click', modifier: 'logo' })}
          >
            <SWSLogo className="w-x20 max-xl:ml-[-66px]" />
          </Link>
        </div>
        <nav className="flex">
          <div
            data-cy-id="main-navigation"
            className={cn(
              'flex',
              canGoBack
                ? 'pl-[235px] max-xl:pl-[90px]'
                : 'pl-[215px] max-xl:pl-[70px]'
            )}
          >
            <Suspense>
              <Nav />
            </Suspense>
          </div>
          <div className="grid grow grid-flow-col grid-cols-[minmax(0,1fr)] gap-x1 pl-x1 pr-[18px]">
            <div className="relative inline-block w-full min-w-[100px] pt-x1_25 max-lg:!w-[30px]">
              <BasicSearch defaultState={<DefaultState />} locale={locale} />
            </div>
            {showLanguageDropdown && <LanguageButton />}
            <UpgradeButton />
            {user.isFetched ? (
              user.data.loggedIn ? (
                <DesktopTopbarAccountAccess
                  location={location}
                  maxViewCompanyReports={
                    access.status === 'success'
                      ? access.data.viewCompanyReports
                      : 0
                  }
                />
              ) : (
                <DesktopTopbarLoggedOut />
              )
            ) : null}
          </div>
        </nav>
      </div>
    </header>
  );
};
